import {
  Box,
  Stack,
  Alert,
  StackSeparator,
  HStack,
  ButtonGroup,
  Text,
  Button,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../shared/stores/RootStore";
import { StreetViewComponent } from "./StreetViewComponent";
import { useState } from "react";
import turf from "../../../shared/utils/turf";
import { splitLineAtDistances } from "../../../map/utils/map-utils";
import { toast } from "../../../shared/views/CustomToaster";

const HlaStreetViewBase = () => {
  const { hlaStore, streetViewStore } = useStores();

  const [loading, setLoading] = useState(false);

  const hlaWay = hlaStore.getSelected()!;
  const data = streetViewStore.distanceToSelectedHla;

  const handleSplit = () => {
    const lineString = turf.lineString(hlaWay.coordinates);

    const newLines = splitLineAtDistances(lineString, [
      data!.distanceAlongLine,
    ]).map((feature) =>
      feature.geometry.coordinates.map(
        (point) =>
          ({
            lat: point[1],
            lng: point[0],
          } as mapboxgl.LngLat),
      ),
    );

    setLoading(true);

    hlaStore
      .cutSelectedWay(newLines)
      .then((ways) => {
        ways && hlaStore.select(ways[1].id);

        toast("success", "Split successful");
      })
      .catch(() => {
        toast("error", "Split failed");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!hlaWay) return null;

  return (
    <Stack height={"50%"} minHeight={"460px"} separator={<StackSeparator />} gap={0}>
      <HStack fontSize={"sm"} p={2}>
        <Text flexGrow={1}>
          {data?.distanceAlongLine.toFixed(2)} meters from the start
        </Text>
        <ButtonGroup size="sm" colorPalette={"primary"} variant={"surface"}>
          <Button
            onClick={handleSplit}
            loading={loading}
            disabled={
              !data ||
              lowPrecisionEquals(data.distanceAlongLine, 0) ||
              lowPrecisionEquals(data.distanceAlongLine, hlaWay.lengthM)
            }
          >
            Split here
          </Button>
        </ButtonGroup>
      </HStack>

      <Box flexGrow={1} flexShrink={0} position="relative">
        <StreetViewComponent />

        {!!data && (
          <Stack
            position="absolute"
            top={3}
            left={3}
            zIndex={1}
            alignItems={"start"}
          >
            {!data.currentPositionIsNear && (
              <Alert.Root size={"sm"} w={"340px"} status={"warning"}>
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Description>
                    You moved away from the selected segment.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
            {!data.currentPositionIsNear && !!data.otherWayNearbyId && (
              <Button
                size="sm"
                fontSize={"sm"}
                variant={"surface"}
                colorPalette={"primary"}
                onClick={() => {
                  hlaStore.select(data.otherWayNearbyId!);
                  streetViewStore.calcDistanceToSelectedHla();
                }}
              >
                Select nearest segment
              </Button>
            )}

            {!!data?.currentPosition &&
              !data?.otherWayNearbyId &&
              !data?.currentPositionIsNear && (
                <Button
                  size="sm"
                  fontSize={"sm"}
                  variant={"surface"}
                  colorPalette={"primary"}
                  onClick={() => {
                    streetViewStore.queryCurrentPosition();
                  }}
                >
                  Create new segment here
                </Button>
              )}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export const HlaStreetView = observer(HlaStreetViewBase);

const lowPrecisionEquals = (n1: number, n2: number, decimals = 1) => {
  return n1.toFixed(decimals) === n2.toFixed(decimals);
};
